import { Box, CircularProgress, CssBaseline, Dialog, DialogContent, Stack, ThemeProvider } from '@mui/material';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, SecureRoute, Security } from '@okta/okta-react';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useHistory } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import WebFont from 'webfontloader';
import AltHome from './AltHome';
import './App.scss';
import AppRoutes from './AppRoutes';
import DateSelectionDialog from './components/DateSelectionDialog';
import Customers from './customers/Customers';
import ExternalHome from './external/Home';
import NavBar from './NavBar';
import SFDC from './sfdc/components/SFDC';
import SFDCMode from './sfdc/SFDCMode';
import Storage from './storage/Storage';
import configStore from './stores/ConfigStore';
import theme from './Theme';
import UserManagement from './userManagement/UserManagement';
import CreateWizard from './wizard/Create';
import ProcessDialog from './wizard/ProcessDialog';

const OKTA_DOMAIN = window.REACT_APP_CONFIG?.REACT_APP_OKTA_DOMAIN || process.env.REACT_APP_OKTA_DOMAIN;
const CLIENT_ID = window.REACT_APP_CONFIG?.REACT_APP_OKTA_CLIENT_ID || process.env.REACT_APP_OKTA_CLIENT_ID;
const CALLBACK_PATH = '/login/callback';

const ISSUER = `https://${OKTA_DOMAIN}/oauth2/default`;
const HOST = window.location.host;
const PROTOCOL = window.location.protocol;
const REDIRECT_URI = `${PROTOCOL}//${HOST}${CALLBACK_PATH}`;
const SCOPES = 'openid profile email';

const okta_config = {
    issuer: ISSUER,
    clientId: CLIENT_ID,
    redirectUri: REDIRECT_URI,
    scopes: SCOPES.split(/\s+/),
};

WebFont.load({
    google: {
        families: ['Noto Sans Display:300,400,500,700'],
    },
});

const oktaAuth = new OktaAuth(okta_config);

const App = () => {
    const { show_progress, showProgress } = configStore;
    const [show_header, showHeader] = useState(true);
    const history = useHistory();
    const [enableScopManageUsers, setEnableScopManageUsers] = useState<boolean>(false);
    const { t } = useTranslation();

    const triggerLogin = async () => {
        await oktaAuth.signInWithRedirect();
    };

    const restoreOriginalUri = async (_oktaAuth: any, originalUri: any) => {
        history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
    };

    const customAuthHandler = async () => {
        const previousAuthState = oktaAuth.authStateManager.getPreviousAuthState();
        if (!previousAuthState || !previousAuthState.isAuthenticated) {
            // App initialization stage
            await triggerLogin();
        } else {
            alert(t('common.logged-out'));
        }
    };

    function handleDismissProgessDialog(event, reason) {
        if (reason && reason === 'backdropClick') {
            return;
        }
        showProgress(false);
    }

    const handleDatesDialogSubmit = (startDate, endDate) => {
        configStore.setStartDate(startDate);
        configStore.setEndDate(endDate);
        configStore.setOpenDateSelection(false);
        configStore.setProcessDialogOpen(true);
    };

    const handleDatesDialogClose = () => {
        configStore.resetConfigData();
        configStore.setOpenDateSelection(false);
    };

    return (
        <div className="App">
            <RecoilRoot>
                <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <Stack direction="row">
                            {show_header ? (
                                <NavBar
                                    configStore={configStore}
                                    enableScopManageUsers={enableScopManageUsers}
                                    setEnableScopManageUsers={setEnableScopManageUsers}
                                />
                            ) : null}
                            <Box sx={{ flexGrow: 1, paddingLeft: '50px' }}>
                                <Switch>
                                    <Route path={CALLBACK_PATH} component={LoginCallback} />
                                    <SecureRoute
                                        path={AppRoutes.configurations}
                                        exact
                                        render={(props) => (
                                            <AltHome
                                                {...props}
                                                progress={showProgress}
                                                configStore={configStore}
                                                enableScopManageUsers={enableScopManageUsers}
                                                setEnableScopManageUsers={setEnableScopManageUsers}
                                            />
                                        )}
                                    />
                                    <SecureRoute
                                        path={AppRoutes.create}
                                        exact
                                        render={(props) => <CreateWizard {...props} configStore={configStore} />}
                                    />
                                    <SecureRoute
                                        path={AppRoutes.edit}
                                        exact
                                        render={(props) => <CreateWizard {...props} configStore={configStore} />}
                                    />
                                    <SecureRoute
                                        path={AppRoutes.sfdc}
                                        exact
                                        render={(props) => (
                                            <SFDC
                                                {...props}
                                                debug
                                                mode={SFDCMode.home}
                                                progress={showProgress}
                                                header={showHeader}
                                            />
                                        )}
                                    />
                                    <SecureRoute
                                        path={AppRoutes.sfdc_source}
                                        exact
                                        render={(props) => (
                                            <SFDC
                                                {...props}
                                                mode={SFDCMode.source}
                                                progress={showProgress}
                                                header={showHeader}
                                            />
                                        )}
                                    />
                                    <SecureRoute
                                        path={AppRoutes.sfdc_target}
                                        exact
                                        render={(props) => (
                                            <SFDC
                                                {...props}
                                                mode={SFDCMode.target}
                                                progress={showProgress}
                                                header={showHeader}
                                            />
                                        )}
                                    />
                                    <SecureRoute
                                        path={AppRoutes.sfdc_source_callback}
                                        exact
                                        render={(props) => (
                                            <SFDC
                                                {...props}
                                                mode={SFDCMode.source_callback}
                                                progress={showProgress}
                                                header={showHeader}
                                            />
                                        )}
                                    />
                                    <SecureRoute
                                        path={AppRoutes.sfdc_target_callback}
                                        exact
                                        render={(props) => (
                                            <SFDC
                                                {...props}
                                                mode={SFDCMode.target_callback}
                                                progress={showProgress}
                                                header={showHeader}
                                            />
                                        )}
                                    />
                                    <SecureRoute
                                        path={AppRoutes.storage}
                                        exact
                                        render={(props) => <Storage {...props} progress={showProgress} />}
                                    />
                                    <SecureRoute
                                        path={AppRoutes.externalHome}
                                        exact
                                        render={(props) => <ExternalHome {...props} progress={showProgress} />}
                                    />
                                    <SecureRoute
                                        path={AppRoutes.customers}
                                        exact
                                        render={(props) => <Customers {...props} progress={showProgress} />}
                                    />
                                    <SecureRoute
                                        path={AppRoutes.user_management}
                                        exact
                                        render={(props) => <UserManagement {...props} progress={showProgress} />}
                                    />
                                    <SecureRoute
                                        path={`${AppRoutes.customers}:mode`}
                                        exact
                                        render={(props) => <Customers {...props} progress={showProgress} />}
                                    />

                                    <Route
                                        path="*"
                                        exact
                                        render={() => (
                                            <main style={{ padding: '1rem' }}>
                                                <p>There's nothing here!</p>
                                            </main>
                                        )}
                                    />
                                </Switch>
                            </Box>
                            {/*show_header && <SideBar/>*/}
                            {
                                <Dialog open={show_progress} onClose={handleDismissProgessDialog}>
                                    <DialogContent>
                                        <CircularProgress data-testid="CircularProgress" color="primary" />
                                    </DialogContent>
                                </Dialog>
                            }
                            <DateSelectionDialog
                                open={configStore.openDateSelection}
                                onClose={() => handleDatesDialogClose()}
                                onSubmit={(startDate: any, endDate: any) => {
                                    handleDatesDialogSubmit(startDate, endDate);
                                }}
                                actionTitle="Process data"
                                actionDescription="data process"
                            />
                        </Stack>
                        {configStore.processDialogOpen ? (
                            <ProcessDialog
                                onClose={() => configStore.setProcessDialogOpen(false)}
                                configStore={configStore}
                            />
                        ) : null}
                    </ThemeProvider>
                </Security>
            </RecoilRoot>
        </div>
    );
};

export default observer(App);
